import { AppIconButton, AppInput } from '@9b3kuq5phwuk4txw/kiban-react-ui'
import { Eye, EyeOff, Lock } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { VALIDATION_MESSAGES } from 'shared'

interface PasswordInputProps {
  value: string
  onChange: (value: string) => void
  onValidChange?: (isValid: boolean) => void
  onEnter?: () => void
  required?: boolean
  tabIndex?: number
  name?: string
  autoComplete?: string
  label?: string
  minLength?: number
  maxLength?: number
}

export function PasswordInput({
  value,
  onChange,
  onValidChange,
  onEnter,
  required = true,
  tabIndex = 2,
  name = 'password',
  autoComplete = 'current-password',
  label = 'パスワード',
  minLength = 8,
  maxLength = 32,
}: PasswordInputProps) {
  const [error, setError] = useState<string | null>(null)
  const [touched, setTouched] = useState(false)
  const [isPasswordVisible, setIsPasswordVisible] = useState(false)

  const validate = useCallback(
    (val: string) => {
      if (required && !val) {
        return VALIDATION_MESSAGES.REQUIRED
      }
      if (val && val.length < minLength) {
        return VALIDATION_MESSAGES.minLength(minLength)
      }
      if (val && val.length > maxLength) {
        return VALIDATION_MESSAGES.maxLength(maxLength)
      }
      return null
    },
    [required, minLength, maxLength],
  )

  useEffect(() => {
    const errorMsg = validate(value)
    if (touched) {
      setError(errorMsg)
    }
    onValidChange?.(errorMsg === null)
  }, [value, touched, onValidChange, validate])

  const handleBlur = () => {
    setTouched(true)
    const errorMsg = validate(value)
    setError(errorMsg)
    onValidChange?.(errorMsg === null)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      onEnter?.()
    }
  }

  return (
    <AppInput
      id={name}
      name={name}
      label={label}
      type={isPasswordVisible ? 'text' : 'password'}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      tabIndex={tabIndex}
      autoComplete={autoComplete}
      leftIcon={<Lock />}
      rightElement={
        <AppIconButton
          className='absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7'
          onClick={() => setIsPasswordVisible(!isPasswordVisible)}
          tabIndex={-1}
          description={isPasswordVisible ? 'パスワードを非表示' : 'パスワードを表示'}
        >
          {isPasswordVisible ? (
            <EyeOff className='size-4 text-muted-foreground' />
          ) : (
            <Eye className='size-4 text-muted-foreground' />
          )}
        </AppIconButton>
      }
      maxLength={maxLength}
      showCharCount
      error={error && touched ? error : undefined}
    />
  )
}
